import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentMethod } from '@stripe/stripe-js';
import { useSelector } from 'react-redux';
import paymentAPI from '../../../deprecated/payment/api';
import { IAppState } from '../../../../store';
import { EVENTS, track, trackSignupFunnel } from 'src/util/track';
import history from '../../../../lib/history';
import User from 'src/models/User';
import { Coupon } from 'src/pages/deprecated/payment/types';
import { TagData } from 'src/store/signup-workflow/types';
// import { validateCoupons } from '../../signup';
import styles from './wallet-component.module.scss';
// import { useAppSelector } from 'src/store/hooks';
import axios from 'axios';
import { decodeCookie } from 'src/lib/cookie-parser';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { validateCoupons } from 'src/util/coupon';
import { checkQuizStatus } from 'src/services/quiz-service';
import { extractErrorMessage, getEnhancedUserData } from 'src/lib/utils';
import { saveShippingAddress } from 'src/services';
import { PLANS } from 'src/pages/constants';
import {
  getPersonalCoachPriceId,
  getNutritionCoachingPriceId,
  getSupplementPackPriceId,
} from 'src/pages/subscription-new/utils/plans.util';
import {
  buildWalletPaymentIntentAddonPriceIds,
  buildWalletPaymentIntentKey,
  isWalletPaymentIntentResponseCurrent,
} from './walletPaymentIntent.util';
import {
  getAllowedCampaignCode,
  getCampaignFromSession,
} from 'src/util/campaign.util';
import {
  setPaymentIntentError,
  setPaymentIntentLoading,
  setPaymentProcessing,
} from 'src/store/payment';

interface WalletSubscriptionType {
  user: User;
  paymentMethod: PaymentMethod;
  priceId: string;
  coupon: Coupon | undefined;
  isChallengeAccepted: boolean;
  totalPrice: string;
  futureCommencement: boolean;
  tagId: number;
  productId: string;
  challengeObject: TagData | null;
}
interface OwnProps {
  priceId: string;
  planName: string;
  finalPrice: number;
  coupon: Coupon | undefined;
  isChallengeAccepted: boolean;
  tagId: number;
  challengeObject: TagData | null;
  futureCommencement: boolean;
  productId: string;
  stripeCustomer: any;
  setWalletPaymentIntentId: (paymentIntent: string) => void;
  popupVisible: boolean;
  isNoIntentAvailable: boolean;
  setIsNoIntentAvailable: (isNoIntentAvailable: boolean) => void;
  isSubmitDisabled?: boolean;
  claimGift?: boolean;
  isSistemaGiftActive: boolean;
  setErrorMessage?: (error: string) => void;
}

const WalletComponent = ({
  priceId,
  finalPrice,
  planName,
  coupon,
  isChallengeAccepted,
  tagId,
  challengeObject,
  futureCommencement,
  productId,
  stripeCustomer,
  setWalletPaymentIntentId,
  popupVisible,
  isNoIntentAvailable,
  setIsNoIntentAvailable,
  isSubmitDisabled,
  claimGift = false,
  isSistemaGiftActive,
  setErrorMessage,
}: OwnProps) => {
  // Redux Selectors
  const user = useSelector((state: IAppState) => state.auth?.user);
  const {
    isFitherProjectUser,
    showFreeTrial,
    isChallengePackageFlow,
    isPersonalCoachingAdded,
    isNutritionCoachingAdded,
    isSupplementAdded,
    allPlansSorted,
    shippingAddress,
    proteinFlavor,
    challengePackageSelectedPlan,
  } = useAppSelector(state => state.signUp);

  const personalCoachPriceId = getPersonalCoachPriceId(allPlansSorted);
  const nutritionCoachingPriceId = getNutritionCoachingPriceId(allPlansSorted);
  const supplementPackPriceId = getSupplementPackPriceId(allPlansSorted);

  const stripe = useStripe();

  // Supplement bundle requires shipping address + protein flavour to be filled in
  const isSupplementBundleAdded =
    isSupplementAdded ||
    (isChallengePackageFlow &&
      (challengePackageSelectedPlan?.id === PLANS.CHALLENGE_WITH_SUPPLEMENTS ||
        isSistemaGiftActive));

  const isSupplementFieldsMissing =
    isSupplementBundleAdded &&
    (!shippingAddress?.firstName ||
      !shippingAddress?.lastName ||
      !shippingAddress?.mobilePhone ||
      !shippingAddress?.streetAddress ||
      !shippingAddress?.suburbCity ||
      !shippingAddress?.stateProvince ||
      !shippingAddress?.postalCode ||
      !shippingAddress?.country ||
      !proteinFlavor);

  const paymentIntentAddonPriceIds = useMemo(
    () =>
      buildWalletPaymentIntentAddonPriceIds({
        isChallengePackageFlow,
        isPersonalCoachingAdded,
        personalCoachPriceId,
        isNutritionCoachingAdded,
        nutritionCoachingPriceId,
        isSupplementAdded,
        supplementPackPriceId,
      }),
    [
      isChallengePackageFlow,
      isPersonalCoachingAdded,
      personalCoachPriceId,
      isNutritionCoachingAdded,
      nutritionCoachingPriceId,
      isSupplementAdded,
      supplementPackPriceId,
    ]
  );

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [paymentDetail, setPaymentDetail] = useState<any>(null);
  const [isLoading, toggleIsLoading] = useState<boolean>(false);
  let cancelTokenSource: any = useRef(null);
  const dispatch = useAppDispatch();

  const desiredIntentKeyRef = useRef<string | null>(null);
  const requestedIntentKeyRef = useRef<string | null>(null);
  const intentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createPaymentIntent = useCallback(
    async (priceId: string, intentKey: string) => {
      if (!priceId || priceId.trim() === '') {
        return;
      }
      toggleIsLoading(true);
      const cookie = decodeCookie();
      if (cancelTokenSource.current) {
        //check if any intent request is going on
        cancelTokenSource.current.cancel(
          'Cancelled previous payment intent request'
        );
      }
      cancelTokenSource.current = axios.CancelToken.source();
      try {
        dispatch(setPaymentIntentLoading(true)); //update redux
        const activeCampaignCode = getAllowedCampaignCode(
          getCampaignFromSession()
        );
        const paymentIntentPayload: Record<string, any> = {
          priceId: priceId,
          customerId: stripeCustomer?.id,
          coupon: coupon?.id,
          isZeroPayment: finalPrice === 0,
          isFreeTrial: showFreeTrial, //free trial
          isChallengePackage: isChallengePackageFlow,
          addonPriceIds: paymentIntentAddonPriceIds,
          claimGift,
        };
        if (activeCampaignCode) {
          paymentIntentPayload.campaignCode = activeCampaignCode;
        }
        /// Used custom axios as axios instant was not working with cancel token
        const data: any = await axios.post(
          'stripe/create/payment-intent',
          paymentIntentPayload,
          {
            headers: { Authorization: (cookie && cookie.token) || '' },
            cancelToken: cancelTokenSource.current.token,
          }
        );
        const isCurrentIntentResponse = isWalletPaymentIntentResponseCurrent({
          responseIntentKey: intentKey,
          desiredIntentKey: desiredIntentKeyRef.current,
        });

        if (isCurrentIntentResponse) {
          dispatch(setPaymentIntentLoading(false));

          setClientSecret(data?.data?.data.clientSecret);
          setIsNoIntentAvailable(data?.data?.data.noIntent);

          setPaymentDetail(data?.data?.data?.subscription?.latest_invoice);
          setWalletPaymentIntentId(
            data?.data?.data?.subscription?.latest_invoice?.payment_intent?.id
          );
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Previous intent request is cancelled');
        } else {
          console.error('Failed to create payment intent', error);
          const status = (error as any)?.response?.status;
          const is4xx =
            (status >= 400 && status < 500) ||
            (error as any)?.response?.data?.code === 422;
          if (is4xx) {
            dispatch(setPaymentProcessing(false));
            const apiMessage =
              extractErrorMessage(error) || 'Unable to process payment.';
            if (
              isWalletPaymentIntentResponseCurrent({
                responseIntentKey: intentKey,
                desiredIntentKey: desiredIntentKeyRef.current,
              })
            ) {
              dispatch(setPaymentIntentError(true));
              setErrorMessage?.(apiMessage);
            }
          }
        }
      } finally {
        if (
          isWalletPaymentIntentResponseCurrent({
            responseIntentKey: intentKey,
            desiredIntentKey: desiredIntentKeyRef.current,
          })
        ) {
          dispatch(setPaymentIntentLoading(false));
          toggleIsLoading(false);
        }
      }
    },
    [
      coupon?.id,
      dispatch,
      finalPrice,
      isChallengePackageFlow,
      paymentIntentAddonPriceIds,
      setIsNoIntentAvailable,
      setWalletPaymentIntentId,
      showFreeTrial,
      stripeCustomer?.id,
      claimGift,
      setErrorMessage,
    ]
  );

  useEffect(() => {
    // to trigger only when the popUp is closed and a plan (priceId) is selected
    if (
      stripe &&
      stripeCustomer &&
      !popupVisible &&
      Boolean(priceId && priceId.trim() !== '')
    ) {
      const intentKey = buildWalletPaymentIntentKey({
        priceId,
        addonPriceIds: paymentIntentAddonPriceIds,
        couponId: coupon?.id,
        claimGift,
      });

      const intentKeyChanged = desiredIntentKeyRef.current !== intentKey;
      if (intentKeyChanged) {
        desiredIntentKeyRef.current = intentKey;
        requestedIntentKeyRef.current = null;
        setClientSecret(null);
        setPaymentDetail(null);
        setWalletPaymentIntentId('');
      }

      if (!intentKeyChanged && requestedIntentKeyRef.current === intentKey) {
        return; // already called for this combination
      }

      dispatch(setPaymentIntentLoading(true));
      dispatch(setPaymentIntentError(false));
      if (intentDebounceRef.current) {
        clearTimeout(intentDebounceRef.current);
      }
      intentDebounceRef.current = setTimeout(() => {
        requestedIntentKeyRef.current = intentKey;
        createPaymentIntent(priceId, intentKey);
      }, 800);
    }
    return () => {
      if (intentDebounceRef.current) {
        clearTimeout(intentDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isChallengePackageFlow,
    popupVisible,
    priceId,
    stripe,
    stripeCustomer,
    paymentIntentAddonPriceIds,
    coupon?.id,
    claimGift,
  ]);

  // Set up Apple Pay / Google Pay payment request.
  useEffect(() => {
    if (!stripe || !paymentDetail) return;

    if (paymentRequest) {
      paymentRequest.update({
        total: { label: planName, amount: paymentDetail.amount_remaining },
      });
    } else {
      const pr = stripe.paymentRequest({
        country: paymentDetail.account_country,
        currency: paymentDetail.currency,
        total: { label: planName, amount: paymentDetail.amount_remaining },
      });

      pr.canMakePayment().then(result => {
        if (result) {
          setPaymentRequest(pr);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentDetail, planName, stripe]);

  const completeWalletPayment = async (props: WalletSubscriptionType) => {
    const {
      user,
      paymentMethod,
      priceId,
      coupon,
      isChallengeAccepted,
      totalPrice,
      futureCommencement,
      tagId,
      productId,
      challengeObject,
    } = props;
    const errorMsg = validateCoupons(priceId, coupon?.metadata);
    if (errorMsg) {
      toggleIsLoading(false);
      return { errorMsg };
    }

    let quizFinalStatus = false;

    //checking quiz status before final payment
    const quizStatusRes = await checkQuizStatus({ email: user.email });
    if (quizStatusRes.status === 200) {
      if (quizStatusRes?.data?.data) {
        quizFinalStatus = true;
      }
    }

    // Personal coaching is bundled into the challenge invoice — include it in priceIDs
    const mainPriceIDs = [priceId];
    if (
      isChallengePackageFlow &&
      isPersonalCoachingAdded &&
      personalCoachPriceId
    ) {
      mainPriceIDs.push(personalCoachPriceId);
    }

    // For standard plan with addons, addon charges were bundled into the
    const nonChallengeAddonPriceIds: string[] = [];
    if (
      !isChallengePackageFlow &&
      isNutritionCoachingAdded &&
      nutritionCoachingPriceId
    ) {
      nonChallengeAddonPriceIds.push(nutritionCoachingPriceId);
    }
    if (!isChallengePackageFlow && isSupplementAdded && supplementPackPriceId) {
      nonChallengeAddonPriceIds.push(supplementPackPriceId);
    }

    const shouldSaveShippingAddress = isSupplementBundleAdded;
    if (shouldSaveShippingAddress && shippingAddress) {
      await saveShippingAddress(
        shippingAddress,
        proteinFlavor ?? undefined,
        isSistemaGiftActive
      );
    }

    const activeCampaignCode = getAllowedCampaignCode(getCampaignFromSession());
    let purchaseInvoicePayload: any = {
      paymentMethodID: paymentMethod.id,
      customerID: stripeCustomer?.id,
      productId: productId,
      priceIDs: mainPriceIDs,
      promoID: coupon?.id ?? null,
      userID: user.id,
      isChallenge: !!isChallengeAccepted,
      paymentIntentId: paymentDetail.payment_intent.id,
      isCardPayment: false, //make it true only if is card payment
      futureCommencement,
      isQuiz: quizFinalStatus, // from web, this will be true
      isChallengePackage: isChallengePackageFlow,
      addonPriceIds: nonChallengeAddonPriceIds,
      claimGift,
    };
    if (activeCampaignCode) {
      purchaseInvoicePayload.campaignCode = activeCampaignCode;
    }
    if (!!isChallengeAccepted && challengeObject?.id) {
      purchaseInvoicePayload.challenge_ids = [challengeObject.id];
    }

    // Process main payment + addon setup (addons already paid via Apple Pay)
    await paymentAPI.post(
      'stripe/invoice/purchase/wallets',
      purchaseInvoicePayload
    );

    trackSignupFunnel(EVENTS.signupPaymentCompleted, {
      payment_method: 'wallet',
      selected_plan_id: priceId,
      is_challenge_package_flow: Boolean(isChallengePackageFlow),
      total_price: totalPrice,
    });
    trackSignupFunnel(EVENTS.signupPurchaseCompleted, {
      payment_method: 'wallet',
      selected_plan_id: priceId,
      is_challenge_package_flow: Boolean(isChallengePackageFlow),
      total_price: totalPrice,
    });

    // Update Google Analytics
    const enhancedUserData = getEnhancedUserData(user);

    if (user.status !== 'PROSPECT') {
      history.push('/success?ref=TransactionComplete', {
        tagId,
        futureCommencement,
        priceId,
        coupon,
        totalPrice,
        dataLayer: track(EVENTS.programPurchase, {
          currency: 'AUD',
          value: totalPrice,
          purchaseComplete: 'ClientRenewal',
          priceIDs: [priceId],
          isFitherProjectUser,
          ...enhancedUserData,
        }),
      });
    } else {
      history.push('/success?ref=TransactionComplete', {
        tagId,
        futureCommencement,
        priceId,
        coupon,
        totalPrice,
        dataLayer: track(EVENTS.programPurchase, {
          currency: 'AUD',
          value: totalPrice,
          purchaseComplete: 'ClientNewPurchase',
          priceIDs: [priceId],
          isFitherProjectUser,
          ...enhancedUserData,
        }),
      });
    }
  };

  const watchPaymentStatus = (clientSecret: string | null) => {
    toggleIsLoading(true);
    if (paymentRequest && stripe && clientSecret) {
      paymentRequest.on(
        'paymentmethod',
        async (ev: { paymentMethod: any; complete: (arg0: any) => void }) => {
          // Confirm the PaymentIntent without handling potential next actions (yet).
          const { error: confirmError } = await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );

          if (!user || confirmError) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            trackSignupFunnel(EVENTS.signupPaymentFailed, {
              payment_method: 'wallet',
              failure_stage: 'payment_authorization',
              error_message: confirmError?.message || 'Wallet payment failed',
              selected_plan_id: priceId,
            });
            ev.complete('fail');
            toggleIsLoading(false);
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            ev.complete('success');
            completeWalletPayment({
              user,
              paymentMethod: ev.paymentMethod,
              priceId,
              coupon,
              isChallengeAccepted,
              totalPrice: finalPrice.toString(),
              futureCommencement,
              tagId,
              productId,
              challengeObject,
            });
          }
        }
      );
      paymentRequest.on('cancel', () => {
        toggleIsLoading(false);
      });
    }
  };

  return (
    <>
      {!!paymentRequest && stripeCustomer && !isNoIntentAvailable && (
        <>
          <div className={styles.walletContainer}>
            {isLoading ? (
              <div className={styles.walletSkeleton} />
            ) : (
              <div
                style={{
                  opacity:
                    isSupplementFieldsMissing || isSubmitDisabled ? 0.4 : 1,
                  pointerEvents:
                    isSupplementFieldsMissing || isSubmitDisabled
                      ? 'none'
                      : 'auto',
                }}>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    style: { paymentRequestButton: { theme: 'dark' } },
                  }}
                  onClick={() => {
                    trackSignupFunnel(EVENTS.signupPaymentMethodSelected, {
                      payment_method: 'wallet',
                      selected_plan_id: priceId,
                      is_challenge_package_flow: Boolean(
                        isChallengePackageFlow
                      ),
                    });
                    trackSignupFunnel(EVENTS.signupPaymentSubmitted, {
                      payment_method: 'wallet',
                      selected_plan_id: priceId,
                      is_challenge_package_flow: Boolean(
                        isChallengePackageFlow
                      ),
                      total_price: finalPrice,
                    });
                    watchPaymentStatus(clientSecret);
                  }}
                />
              </div>
            )}
          </div>
          <div className={`${styles.or}`}> OR </div>
        </>
      )}
    </>
  );
};

export default WalletComponent;
